// src/components/channels/ConfigureMessenger.js

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  AlertCircle, 
  CheckCircle2, 
  Loader2, 
  ExternalLink, 
  ArrowLeft,
  Copy,
  Info,
  Lock
} from 'lucide-react';
import { channelService } from '../../services/PlatformService';

const ConfigureMessenger = () => {
  const { channelId } = useParams();
  const navigate = useNavigate();
  
  const [channel, setChannel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const [currentStep, setCurrentStep] = useState(1);
  const [copied, setCopied] = useState(false);
  
  const [formData, setFormData] = useState({
    pageAccessToken: '',
    pageId: ''
  });

  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    loadChannel();
  }, [channelId]);

  const loadChannel = async () => {
    try {
      setLoading(true);
      const response = await channelService.getChannel(channelId);
      setChannel(response.data);
      
      // Se já tem configuração, preencher form
      if (response.data.config) {
        setFormData({
          pageAccessToken: response.data.config.page_access_token || '',
          pageId: response.data.config.page_id || ''
        });
        
        // Determinar step baseado no status
        if (response.data.status === 'active') {
          setCurrentStep(3); // Já configurado
        } else if (response.data.config.page_access_token && response.data.config.page_id) {
          setCurrentStep(2); // Credenciais salvas, falta webhook
        }
      }
    } catch (error) {
      console.error('Error loading channel:', error);
      setError('Erro ao carregar canal');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
    setTestResult(null);
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setError('');
    setTestResult(null);

    try {
      const result = await channelService.testMessengerConnection({
        page_access_token: formData.pageAccessToken,
        page_id: formData.pageId
      });

      if (result.success) {
        setTestResult({
          success: true,
          message: 'Conexão estabelecida com sucesso!',
          details: result.page_info
        });
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Erro ao testar conexão');
      setTestResult({
        success: false,
        message: 'Falha na conexão'
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSaveCredentials = async () => {
    //if (!testResult?.success) {
    //  setError('Teste a conexão antes de continuar');
    //  return;
    //}

    setSaving(true);
    setError('');

    try {
      // ✅ CORREÇÃO: Atualizar canal existente com as credenciais
      await channelService.updateChannel(channelId, {
        config: {
          page_access_token: formData.pageAccessToken,
          page_id: formData.pageId
        }
      });

      // Recarregar dados do canal
      await loadChannel();
      
      // Avançar para próximo step
      setCurrentStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Erro ao salvar credenciais');
    } finally {
      setSaving(false);
    }
  };

  const handleVerifyWebhook = async () => {
    try {
      setLoading(true);
      const response = await channelService.getChannel(channelId);
      const channelData = response.data;
      
      if (channelData.webhook_verified && channelData.status === 'active') {
        setChannel(channelData);
        setCurrentStep(3);
      } else {
        alert('Configure o webhook no Meta App Dashboard. A verificação ainda não foi concluída.');
      }
    } catch (error) {
      console.error('Error verifying webhook:', error);
      alert('Erro ao verificar webhook');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (loading && !channel) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-600">Carregando canal...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/channels')}
            className="p-2 hover:bg-gray-100 rounded-xl transition-all"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.477 2 2 6.145 2 11.243c0 2.912 1.45 5.52 3.717 7.228V22l3.406-1.871c.91.252 1.872.388 2.877.388 5.523 0 10-4.145 10-9.243C22 6.145 17.523 2 12 2zm1.194 12.431l-2.594-2.77-5.063 2.77 5.57-5.912 2.656 2.77 4.999-2.77-5.568 5.912z"/>
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Configurar {channel?.name}
              </h1>
              <p className="text-sm text-gray-600">Facebook Messenger</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Step 1: Credenciais */}
        {currentStep === 1 && (
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6">
              Etapa 1: Configure suas Credenciais
            </h2>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
              <h3 className="font-semibold text-blue-900 mb-3 flex items-center">
                <ExternalLink className="w-5 h-5 mr-2" />
                Como obter suas credenciais
              </h3>
              <ol className="space-y-2 text-sm text-blue-800">
                <li className="flex items-start">
                  <span className="font-semibold mr-2">1.</span>
                  <span>Acesse o <a href="https://developers.facebook.com/apps" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-600">Meta App Dashboard</a></span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">2.</span>
                  <span>Crie um App (se não tiver) e adicione o produto "Messenger"</span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">3.</span>
                  <span>Vá em <strong>Messenger → Settings</strong></span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">4.</span>
                  <span>Em "Access Tokens", selecione sua Página e clique em <strong>Generate Token</strong></span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">5.</span>
                  <span>Copie o <strong>Page Access Token</strong> e o <strong>Page ID</strong></span>
                </li>
              </ol>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Page Access Token *
                </label>
                <input
                  type="text"
                  name="pageAccessToken"
                  value={formData.pageAccessToken}
                  onChange={handleInputChange}
                  placeholder="EAAD..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  Token de acesso da sua Página do Facebook
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Page ID *
                </label>
                <input
                  type="text"
                  name="pageId"
                  value={formData.pageId}
                  onChange={handleInputChange}
                  placeholder="123456789012345"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  ID numérico da sua Página
                </p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
                  <AlertCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-red-800">{error}</div>
                </div>
              )}

              {testResult && (
                <div className={`border rounded-lg p-4 flex items-start ${
                  testResult.success 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-red-50 border-red-200'
                }`}>
                  {testResult.success ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <p className={`text-sm font-medium ${
                      testResult.success ? 'text-green-800' : 'text-red-800'
                    }`}>
                      {testResult.message}
                    </p>
                    {testResult.details && (
                      <p className="text-xs text-green-700 mt-1">
                        Página: {testResult.details.page_name}
                      </p>
                    )}
                  </div>
                </div>
              )}

              <div className="flex gap-3">

                <button
                  onClick={handleSaveCredentials}
                  disabled={saving}
                  className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    'Salvar e Continuar'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Webhook */}
        {currentStep === 2 && (
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6">
              Etapa 2: Configure o Webhook
            </h2>

            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
              <h4 className="font-semibold text-yellow-900 mb-3 flex items-center">
                <Info className="w-5 h-5 mr-2" />
                Configuração do Webhook (IMPORTANTE)
              </h4>
              <ol className="space-y-3 text-sm text-yellow-800">
                <li className="flex items-start">
                  <span className="font-semibold mr-2">1.</span>
                  <span>No Meta App Dashboard, vá em <strong>Messenger → Settings</strong></span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">2.</span>
                  <span>Na seção "Webhooks", clique em <strong>Add Callback URL</strong></span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">3.</span>
                  <div className="flex-1">
                    <span>Cole esta URL no campo <strong>Callback URL</strong>:</span>
                    <div className="bg-white border border-yellow-300 rounded p-2 mt-2 font-mono text-xs break-all flex items-center justify-between">
                      <span>{channel?.webhook_url}</span>
                      <button
                        onClick={() => copyToClipboard(channel?.webhook_url)}
                        className="ml-2 p-1 hover:bg-yellow-100 rounded"
                      >
                        {copied ? (
                          <CheckCircle2 className="w-4 h-4 text-green-600" />
                        ) : (
                          <Copy className="w-4 h-4 text-yellow-700" />
                        )}
                      </button>
                    </div>
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">4.</span>
                  <div className="flex-1">
                    <span>Cole este token no campo <strong>Verify Token</strong>:</span>
                    <div className="bg-white border border-yellow-300 rounded p-2 mt-2 font-mono text-xs flex items-center justify-between">
                      <span className="font-bold">QUORAAI_VERIFY_TOKEN</span>
                      <button
                        onClick={() => copyToClipboard('QUORAAI_VERIFY_TOKEN')}
                        className="ml-2 p-1 hover:bg-yellow-100 rounded"
                      >
                        <Copy className="w-4 h-4 text-yellow-700" />
                      </button>
                    </div>
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">5.</span>
                  <span>Clique em <strong>Verify and Save</strong></span>
                </li>
                <li className="flex items-start">
                  <span className="font-semibold mr-2">6.</span>
                  <span>Em "Webhook Fields", assine: <strong>messages, messaging_postbacks, messaging_optins, message_deliveries, message_reads</strong></span>
                </li>
              </ol>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start mb-6">
                <AlertCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-red-800">{error}</div>
              </div>
            )}

            <div className="flex items-center justify-between">
              <a 
                href="https://developers.facebook.com/docs/messenger-platform/webhooks" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
              >
                Ver tutorial completo <ExternalLink className="w-4 h-4" />
              </a>

              <button
                onClick={handleVerifyWebhook}
                disabled={loading}
                className="px-6 py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Verificando...
                  </>
                ) : (
                  <>
                    <Lock className="w-5 h-5" />
                    Configurei o Webhook
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Sucesso */}
        {currentStep === 3 && (
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-green-600" />
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Canal Configurado com Sucesso! 🎉
            </h2>
            
            <p className="text-gray-600 mb-8">
              Seu funcionário está pronto para atender no Facebook Messenger
            </p>
            
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={() => navigate('/channels')}
                className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-all"
              >
                Voltar aos Canais
              </button>
              
              <button
                onClick={() => navigate(`/channels/${channelId}`)}
                className="px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-all"
              >
                Ver Detalhes do Canal
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConfigureMessenger;